import React from 'react';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const sanitize = (html) => DOMPurify.sanitize(html);

/**
 * Compact, legal-document-style layout.
 * Sections use hierarchical numbering (e.g. 1., 1.1, 1.2) and tight spacing.
 *
 * Sections structure:
 * [
 *   { number: '1', title: 'Section title', blocks: [
 *      { type: 'p', text: '...' },
 *      { type: 'sub', number: '1.1', title: '...', blocks: [...] },
 *      { type: 'list', items: [...] },
 *   ]}
 * ]
 */
const renderBlock = (block, key) => {
  if (block.type === 'p') {
    return (
      <p
        key={key}
        className="text-sm text-slate-700 leading-relaxed mb-2"
        dangerouslySetInnerHTML={{ __html: sanitize(block.text) }}
      />
    );
  }
  if (block.type === 'list') {
    return (
      <ul key={key} className="list-disc pl-5 mb-2 space-y-0.5 text-sm text-slate-700">
        {block.items.map((it, k) => (
          <li key={it} dangerouslySetInnerHTML={{ __html: sanitize(it) }} />
        ))}
      </ul>
    );
  }
  if (block.type === 'sub') {
    return (
      <div key={key} className="mt-3 mb-2">
        <h3 className="text-sm font-semibold text-[#1e2d3b] mb-1">
          {block.number}. {block.title}
        </h3>
        <div className="pl-1">
          {block.blocks.map((b, i) => renderBlock(b, i))}
        </div>
      </div>
    );
  }
  if (block.type === 'address') {
    return (
      <address key={key} className="not-italic text-sm text-slate-700 leading-relaxed">
        {block.lines.map((line, k) => (
          <div key={line} dangerouslySetInnerHTML={{ __html: sanitize(line) }} />
        ))}
      </address>
    );
  }
  return null;
};

const LegalPage = ({ title, lastUpdated, intro, sections, onRequestDemo, onRequestQuote }) => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${title} — ctrl.aero`}
        description={intro && intro[0] ? intro[0].replace(/<[^>]+>/g, '').slice(0, 300) : title}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      <section className="pt-24 pb-6 sm:pt-28 sm:pb-8 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">{title}</h1>
          {lastUpdated && (
            <p className="text-blue-300 text-xs sm:text-sm">Last Updated: {lastUpdated}</p>
          )}
        </div>
      </section>

      <section className="py-8 sm:py-10 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {intro && (
            <div className="mb-6 text-sm text-slate-700 leading-relaxed space-y-2 border-l-2 border-blue-200 pl-4">
              {intro.map((para, i) => (
                <p key={para} dangerouslySetInnerHTML={{ __html: sanitize(para) }} />
              ))}
            </div>
          )}

          <div className="space-y-5">
            {sections.map((section) => (
              <div key={`${section.number}-${section.title}`}>
                <h2 className="text-base sm:text-lg font-bold text-[#1e2d3b] mb-2 uppercase tracking-wide">
                  {section.number}. {section.title}
                </h2>
                <div>
                  {section.blocks.map((block, j) => renderBlock(block, j))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalPage;
